import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { storeReferralCode, getStoredReferralCode, trackReferral, clearStoredReferralCode } from '@/lib/referralUtils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Loader2, Mail, BookOpen, KeyRound } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { AuthDebug } from '@/components/AuthDebug';
import { PasswordInput } from '@/components/ui/password-input';
import { Checkbox } from '@/components/ui/checkbox';

const emailSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

// Password must be 8+ chars, with uppercase, lowercase, and a number
const passwordRequirements = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .regex(/[a-z]/, 'Password must contain a lowercase letter')
  .regex(/[A-Z]/, 'Password must contain an uppercase letter')
  .regex(/[0-9]/, 'Password must contain a number');

const passwordSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: passwordRequirements,
});

const signupPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: passwordRequirements,
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

const updatePasswordSchema = z.object({
  password: passwordRequirements,
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type EmailForm = z.infer<typeof emailSchema>;
type PasswordForm = z.infer<typeof passwordSchema>;
type SignupPasswordForm = z.infer<typeof signupPasswordSchema>;
type UpdatePasswordForm = z.infer<typeof updatePasswordSchema>;

export default function Auth() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  // Initialize mode synchronously from URL to prevent race condition with redirect
  const [mode, setMode] = useState<'signup' | 'signin' | 'reset' | 'update-password'>(() => {
    const urlMode = searchParams.get('mode');
    if (urlMode === 'update-password') return 'update-password';
    if (urlMode === 'signin') return 'signin';
    if (urlMode === 'reset') return 'reset';
    return 'signup';
  });
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [usePassword, setUsePassword] = useState(true);
  const [passwordUpdated, setPasswordUpdated] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);

  // Capture referral code from URL on mount and handle mode changes
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      storeReferralCode(refCode);
    }
    
    // Update mode if URL changes (e.g., user clicks "Back to Sign In")
    const urlMode = searchParams.get('mode');
    if (urlMode === 'signin' || urlMode === 'signup' || urlMode === 'reset' || urlMode === 'update-password') {
      setMode(urlMode);
    }
  }, [searchParams]);

  const emailForm = useForm<EmailForm>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      email: '',
    },
  });

  const passwordForm = useForm<PasswordForm>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const signupPasswordForm = useForm<SignupPasswordForm>({
    resolver: zodResolver(signupPasswordSchema),
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
    },
  });

  const updatePasswordForm = useForm<UpdatePasswordForm>({
    resolver: zodResolver(updatePasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  // Redirect if already authenticated and process referral (but not if updating password)
  useEffect(() => {
    if (user && !authLoading && mode !== 'update-password') {
      // Check for stored referral code and track it
      const storedRef = getStoredReferralCode();
      if (storedRef) {
        trackReferral(storedRef, user.id).then((success) => {
          if (success) {
            clearStoredReferralCode();
            toast({
              title: 'Welcome to Dino!',
              description: 'You were invited by a friend. Enjoy reading together!',
            });
          }
        });
      }
      navigate('/', { replace: true });
    }
  }, [user, authLoading, navigate, mode]);

  // Early return while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  // Redirect authenticated users (unless updating password)
  if (user && mode !== 'update-password') {
    return null;
  }

  const handleMagicLink = async (data: EmailForm) => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: data.email,
        options: {
          emailRedirectTo: import.meta.env.PROD ? 'https://dinoreads.com/' : `${window.location.origin}/`,
        },
      });
      
      if (error) throw error;

      setMagicLinkSent(true);
      toast({
        title: "Magic link sent!",
        description: "Check your email for a sign-in link.",
      });
    } catch (error: any) {
      console.error('Magic link error:', error);
      
      // Handle email service errors (hook 500 errors)
      let description = error.message || "Failed to send magic link. Please try again.";
      if (error.message?.includes('hook') || error.message?.includes('500')) {
        description = "Our email service is temporarily unavailable. Please try signing up with a password instead, or try again in a few minutes.";
      }
      
      toast({
        title: "Error",
        description,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (data: EmailForm) => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(data.email, {
        redirectTo: import.meta.env.PROD ? 'https://dinoreads.com/auth?mode=update-password' : `${window.location.origin}/auth?mode=update-password`,
      });

      if (error) throw error;

      setResetEmailSent(true);
      toast({
        title: "Password reset email sent!",
        description: "Check your inbox for instructions to reset your password.",
      });
    } catch (error: any) {
      console.error('Password reset error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to send password reset email. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (data: UpdatePasswordForm) => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: data.password,
      });

      if (error) throw error;

      setPasswordUpdated(true);
      toast({
        title: "Password updated!",
        description: "Your password has been successfully changed.",
      });

      // Redirect after a short delay
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 2000);
    } catch (error: any) {
      console.error('Password update error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update password. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordAuth = async (data: PasswordForm, authMode: 'signIn' | 'signUp') => {
    setLoading(true);
    try {
      let result;
      
      if (authMode === 'signUp') {
        result = await supabase.auth.signUp({
          email: data.email,
          password: data.password,
          options: {
            emailRedirectTo: import.meta.env.PROD ? 'https://dinoreads.com/' : `${window.location.origin}/`,
          },
        });
      } else {
        result = await supabase.auth.signInWithPassword({
          email: data.email,
          password: data.password,
        });
        
        // Store remember me preference
        if (!result.error) {
          if (rememberMe) {
            localStorage.setItem('dino_remember_me', 'true');
            sessionStorage.setItem('dino_session_active', 'true');
          } else {
            localStorage.setItem('dino_remember_me', 'false');
            sessionStorage.setItem('dino_session_active', 'true');
          }
        }
      }
      
      if (result.error) throw result.error;

      // Ensure profile exists for BOTH sign up and sign in
      if (result.data.user) {
        try {
          await supabase.rpc('ensure_user_profile');
        } catch (profileError: any) {
          console.error('Profile creation error:', profileError);
          // Don't block the flow for profile creation errors
          // The AuthCheck component will handle redirecting to profile setup
        }
        
        // Handle referral code if present (for signUp)
        if (authMode === 'signUp') {
          const storedRef = getStoredReferralCode();
          if (storedRef) {
            trackReferral(storedRef, result.data.user.id).then((success) => {
              if (success) {
                clearStoredReferralCode();
              }
            });
          }
        }
      }

      if (authMode === 'signUp') {
        toast({
          title: "Account created!",
          description: "Welcome to Dino! Let's set up your profile.",
        });
        
        // Explicitly navigate to onboarding after a brief delay to ensure auth state is settled
        setTimeout(() => {
          navigate('/onboarding/profile', { replace: true });
        }, 500);
      }
      // Sign-in flow: the existing useEffect will handle redirect to home
    } catch (error: any) {
      console.error('Password auth error:', error);
      
      // Show specific error details for diagnostics
      let description = error.message || `Failed to ${authMode === 'signUp' ? 'sign up' : 'sign in'}. Please try again.`;
      
      // Add error code for debugging if available
      if (error.code) {
        description = `${error.code}: ${description}`;
      }
      
      // Handle specific error cases
      if (error.message?.includes('duplicate key value violates unique constraint')) {
        description = "Database constraint error: This might be due to duplicate data. Please try again or contact support.";
      } else if (error.message?.includes('shelves_user_id_name_key')) {
        description = "Database error: Duplicate shelf creation detected. This has been logged for investigation.";
      } else if (error.code === 'unexpected_failure') {
        description = "Unexpected database error occurred. Please try again in a moment.";
      }

      toast({
        title: "Authentication Error",
        description,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSignup = async (data: SignupPasswordForm) => {
    setLoading(true);
    try {
      const result = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          emailRedirectTo: import.meta.env.PROD ? 'https://dinoreads.com/' : `${window.location.origin}/`,
        },
      });
      
      if (result.error) throw result.error;

      // Ensure profile exists
      if (result.data.user) {
        try {
          await supabase.rpc('ensure_user_profile');
        } catch (profileError: any) {
          console.error('Profile creation error:', profileError);
        }
        
        // Handle referral code if present
        const storedRef = getStoredReferralCode();
        if (storedRef) {
          trackReferral(storedRef, result.data.user.id).then((success) => {
            if (success) {
              clearStoredReferralCode();
            }
          });
        }
      }

      toast({
        title: "Account created!",
        description: "Welcome to Dino! Let's set up your profile.",
      });

      // Explicitly navigate to onboarding after a brief delay to ensure auth state is settled
      setTimeout(() => {
        navigate('/onboarding/profile', { replace: true });
      }, 500);
    } catch (error: any) {
      console.error('Password signup error:', error);
      
      let description = error.message || "Failed to create account. Please try again.";
      
      if (error.message?.includes('User already registered')) {
        description = "An account with this email already exists. Try signing in instead.";
      } else if (error.message?.includes('hook') || error.message?.includes('500')) {
        description = "Email verification is temporarily unavailable. Your account was created - please try signing in with your password.";
      }

      toast({
        title: "Sign Up Error",
        description,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md card-elevated" role="main" aria-label="Authentication form">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-primary rounded-full flex items-center justify-center" aria-hidden="true">
            <BookOpen className="w-8 h-8 text-primary-foreground" />
          </div>
          <div className="space-y-2">
            <CardTitle className="text-2xl font-heading">
              {mode === 'signup' && 'Start Your Reading Journey 📚'}
              {mode === 'signin' && 'Welcome Back! 👋'}
              {mode === 'reset' && 'Reset Your Password 🔐'}
              {mode === 'update-password' && 'Set Your New Password 🔑'}
            </CardTitle>
            <CardDescription>
              {mode === 'signup' && 'Join thousands of readers tracking their books, building streaks, and discovering their next favorite read!'}
              {mode === 'signin' && 'Sign in to continue your reading adventure.'}
              {mode === 'reset' && "Enter your email address and we'll send you a link to reset your password."}
              {mode === 'update-password' && 'Choose a new password for your account.'}
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Update Password Flow */}
          {mode === 'update-password' && (
            <>
              {passwordUpdated ? (
                <div className="text-center space-y-4 animate-fade-in">
                  <div className="mx-auto w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                    <KeyRound className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="font-semibold">Password Updated!</h3>
                    <p className="text-sm text-muted-foreground">
                      Your password has been changed successfully. Redirecting you to the app...
                    </p>
                  </div>
                </div>
              ) : (
                <form onSubmit={updatePasswordForm.handleSubmit(handleUpdatePassword)} className="space-y-4 animate-fade-in" aria-label="Update password form">
                  <div className="space-y-2">
                    <Label htmlFor="new-password">New Password</Label>
                    <PasswordInput
                      id="new-password"
                      placeholder="Enter your new password"
                      {...updatePasswordForm.register('password')}
                      disabled={loading}
                      aria-required="true"
                      aria-describedby={updatePasswordForm.formState.errors.password ? "new-password-error" : undefined}
                    />
                    {updatePasswordForm.formState.errors.password && (
                      <p id="new-password-error" className="text-sm text-destructive" role="alert">
                        {updatePasswordForm.formState.errors.password.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirm Password</Label>
                    <PasswordInput
                      id="confirm-password"
                      placeholder="Confirm your new password"
                      {...updatePasswordForm.register('confirmPassword')}
                      disabled={loading}
                      aria-required="true"
                      aria-describedby={updatePasswordForm.formState.errors.confirmPassword ? "confirm-password-error" : undefined}
                    />
                    {updatePasswordForm.formState.errors.confirmPassword && (
                      <p id="confirm-password-error" className="text-sm text-destructive" role="alert">
                        {updatePasswordForm.formState.errors.confirmPassword.message}
                      </p>
                    )}
                  </div>

                  <div className="text-xs text-muted-foreground space-y-1 p-3 bg-muted/50 rounded-md">
                    <p className="font-medium">Password requirements:</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      <li>At least 8 characters</li>
                      <li>One uppercase letter</li>
                      <li>One lowercase letter</li>
                      <li>One number</li>
                      <li>Not a commonly used or breached password</li>
                    </ul>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12"
                    disabled={loading}
                    aria-label="Update password"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <KeyRound className="w-5 h-5 mr-2" />
                        Update Password
                      </>
                    )}
                  </Button>
                </form>
              )}
            </>
          )}

          {/* Password Reset Flow */}
          {mode === 'reset' && (
            <>
              {resetEmailSent ? (
                <div className="text-center space-y-4 animate-fade-in">
                  <div className="mx-auto w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="font-semibold">Check your email</h3>
                    <p className="text-sm text-muted-foreground">
                      We sent password reset instructions to {emailForm.getValues('email')}
                    </p>
                  </div>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setResetEmailSent(false);
                      setMode('signin');
                    }}
                    className="w-full"
                  >
                    Back to Sign In
                  </Button>
                </div>
              ) : (
                <form onSubmit={emailForm.handleSubmit(handlePasswordReset)} className="space-y-4 animate-fade-in" aria-label="Password reset form">
                  <div className="space-y-2">
                    <Label htmlFor="reset-email">Email address</Label>
                    <Input
                      id="reset-email"
                      type="email"
                      placeholder="Enter your email"
                      {...emailForm.register('email')}
                      disabled={loading}
                      aria-required="true"
                      aria-describedby={emailForm.formState.errors.email ? "reset-email-error" : undefined}
                    />
                    {emailForm.formState.errors.email && (
                      <p id="reset-email-error" className="text-sm text-destructive" role="alert">
                        {emailForm.formState.errors.email.message}
                      </p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12"
                    disabled={loading}
                    aria-label="Send password reset link"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Mail className="w-5 h-5 mr-2" />
                        Send Reset Link
                      </>
                    )}
                  </Button>

                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      setMode('signin');
                      emailForm.reset();
                    }}
                    className="w-full"
                  >
                    Back to Sign In
                  </Button>
                </form>
              )}
            </>
          )}

          {/* Sign Up Flow - Password Required */}
          {mode === 'signup' && (
            <form onSubmit={signupPasswordForm.handleSubmit(handlePasswordSignup)} className="space-y-4 animate-fade-in" aria-label="Sign up with password form">
              <div className="space-y-2">
                <Label htmlFor="signup-email">Email address</Label>
                <Input
                  id="signup-email"
                  type="email"
                  placeholder="Enter your email"
                  {...signupPasswordForm.register('email')}
                  disabled={loading}
                  aria-required="true"
                />
                {signupPasswordForm.formState.errors.email && (
                  <p className="text-sm text-destructive" role="alert">
                    {signupPasswordForm.formState.errors.email.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="signup-password">Password</Label>
                <PasswordInput
                  id="signup-password"
                  placeholder="Create a password"
                  {...signupPasswordForm.register('password')}
                  disabled={loading}
                  aria-required="true"
                />
                {signupPasswordForm.formState.errors.password && (
                  <p className="text-sm text-destructive" role="alert">
                    {signupPasswordForm.formState.errors.password.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="signup-confirm-password">Confirm Password</Label>
                <PasswordInput
                  id="signup-confirm-password"
                  placeholder="Confirm your password"
                  {...signupPasswordForm.register('confirmPassword')}
                  disabled={loading}
                  aria-required="true"
                />
                {signupPasswordForm.formState.errors.confirmPassword && (
                  <p className="text-sm text-destructive" role="alert">
                    {signupPasswordForm.formState.errors.confirmPassword.message}
                  </p>
                )}
              </div>

              <div className="text-xs text-muted-foreground space-y-1 p-3 bg-muted/50 rounded-md">
                <p className="font-medium">Password requirements:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>At least 8 characters</li>
                  <li>One uppercase letter</li>
                  <li>One lowercase letter</li>
                  <li>One number</li>
                </ul>
              </div>

              <Button
                type="submit"
                className="w-full h-12"
                disabled={loading}
                aria-label="Create account"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <KeyRound className="w-5 h-5 mr-2" />
                    Create Account
                  </>
                )}
              </Button>
            </form>
          )}

          {/* Sign In Flow - Password Primary, Magic Link Backup */}
          {mode === 'signin' && (
            <>
              {usePassword ? (
                <form 
                  onSubmit={passwordForm.handleSubmit((data) => handlePasswordAuth(data, 'signIn'))} 
                  className="space-y-4 animate-fade-in"
                  aria-label="Sign in with password form"
                >
                  <div className="space-y-2">
                    <Label htmlFor="signin-email">Email address</Label>
                    <Input
                      id="signin-email"
                      type="email"
                      placeholder="Enter your email"
                      {...passwordForm.register('email')}
                      disabled={loading}
                      aria-required="true"
                      aria-describedby={passwordForm.formState.errors.email ? "signin-email-error" : undefined}
                    />
                    {passwordForm.formState.errors.email && (
                      <p id="signin-email-error" className="text-sm text-destructive" role="alert">
                        {passwordForm.formState.errors.email.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signin-password">Password</Label>
                    <PasswordInput
                      id="signin-password"
                      placeholder="Enter your password"
                      {...passwordForm.register('password')}
                      disabled={loading}
                      aria-required="true"
                      aria-describedby={passwordForm.formState.errors.password ? "signin-password-error" : undefined}
                    />
                    {passwordForm.formState.errors.password && (
                      <p id="signin-password-error" className="text-sm text-destructive" role="alert">
                        {passwordForm.formState.errors.password.message}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="remember-me"
                      checked={rememberMe}
                      onCheckedChange={(checked) => setRememberMe(checked === true)}
                    />
                    <Label 
                      htmlFor="remember-me" 
                      className="text-sm font-normal cursor-pointer"
                    >
                      Remember me
                    </Label>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12"
                    disabled={loading}
                    aria-label="Sign in to your account"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      'Sign In'
                    )}
                  </Button>

                  <div className="flex items-center justify-between text-xs">
                    <button
                      type="button"
                      onClick={() => {
                        const email = passwordForm.getValues('email');
                        if (email) {
                          emailForm.setValue('email', email);
                        }
                        setMode('reset');
                        passwordForm.reset();
                      }}
                      className="text-primary hover:underline"
                      aria-label="Forgot password? Reset it here"
                    >
                      Forgot password?
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const email = passwordForm.getValues('email');
                        if (email) {
                          emailForm.setValue('email', email);
                        }
                        setUsePassword(false);
                      }}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      Use magic link instead
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  {magicLinkSent ? (
                    <div className="text-center space-y-4 animate-fade-in">
                      <div className="mx-auto w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                        <Mail className="w-6 h-6 text-primary" />
                      </div>
                      <div className="space-y-2">
                        <h3 className="font-semibold">Check your email</h3>
                        <p className="text-sm text-muted-foreground">
                          We sent a magic link to {emailForm.getValues('email')}
                        </p>
                      </div>
                      <Button 
                        variant="outline" 
                        onClick={() => setMagicLinkSent(false)}
                        className="w-full"
                      >
                        Send another link
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={emailForm.handleSubmit(handleMagicLink)} className="space-y-4 animate-fade-in" aria-label="Sign in with magic link form">
                      <div className="space-y-2">
                        <Label htmlFor="signin-magic-email">Email address</Label>
                        <Input
                          id="signin-magic-email"
                          type="email"
                          placeholder="Enter your email"
                          {...emailForm.register('email')}
                          disabled={loading}
                          aria-required="true"
                          aria-describedby={emailForm.formState.errors.email ? "signin-magic-email-error" : undefined}
                        />
                        {emailForm.formState.errors.email && (
                          <p id="signin-magic-email-error" className="text-sm text-destructive" role="alert">
                            {emailForm.formState.errors.email.message}
                          </p>
                        )}
                      </div>

                      <Button
                        type="submit"
                        className="w-full h-12"
                        disabled={loading}
                        aria-label="Send magic link"
                      >
                        {loading ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <Mail className="w-5 h-5 mr-2" />
                            Send Magic Link
                          </>
                        )}
                      </Button>

                      <p className="text-xs text-center text-muted-foreground">
                        We'll email you a link to sign in without a password.
                      </p>

                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => {
                          const email = emailForm.getValues('email');
                          if (email) {
                            passwordForm.setValue('email', email);
                          }
                          setUsePassword(true);
                        }}
                        className="w-full text-xs"
                      >
                        Back to password sign in
                      </Button>
                    </form>
                  )}
                </>
              )}
            </>
          )}

          {mode !== 'reset' && mode !== 'update-password' && (
            <>
              <Separator role="presentation" />

              {/* Mode Toggle */}
              <div className="text-center">
                <p className="text-sm text-muted-foreground">
                  {mode === 'signup' ? 'Already have an account?' : 'New to Dino?'}
                </p>
                <Button
                  variant="link"
                  onClick={() => {
                    setMode(mode === 'signup' ? 'signin' : 'signup');
                    setMagicLinkSent(false);
                    setResetEmailSent(false);
                    setUsePassword(true);
                    emailForm.reset();
                    passwordForm.reset();
                    signupPasswordForm.reset();
                  }}
                  className="text-primary font-semibold"
                  aria-label={mode === 'signup' ? 'Switch to sign in' : 'Switch to sign up'}
                >
                  {mode === 'signup' ? 'Sign in instead' : 'Create an account'}
                </Button>
              </div>
            </>
          )}

          <div className="text-xs text-center text-muted-foreground">
            By continuing, you agree to our{' '}
            <a href="/terms-of-service" className="underline hover:text-foreground" aria-label="Read our Terms of Service">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy-policy" className="underline hover:text-foreground" aria-label="Read our Privacy Policy">
              Privacy Policy
            </a>
          </div>
        </CardContent>
      </Card>
      
      {/* Development debug panel */}
      <AuthDebug />
    </div>
  );
}
